import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { Box } from '@mui/material';
import type { FilterBarFilterOption } from '../../contracts';
import { FilterCondition } from 'framework/types';
import useDebouncedValue from '../../../../hooks/useDebouncedValue';
import { SEARCH_DEBOUNCE_MS } from '../../../../constants';
import AutosuggestionInput from './AutosuggestionInput';
import AutosuggestionResults from './AutosuggestionResults';
import {
  AutosuggestionSearchStatus,
  type AutosuggestionFilterEditorProps,
  type AutosuggestionSearchState,
} from './types';

const AutosuggestionFilterEditor = ({ filter, value, onChange, onClose }: AutosuggestionFilterEditorProps) => {
  const { label, loadOptions, minSearchLength = 0, resolveOption } = filter;
  const multiple = filter.condition === FilterCondition.IN || filter.condition === FilterCondition.OR;
  const selectedValue = typeof value === 'string' ? value : '';
  const [selectedOption, setSelectedOption] = useState<FilterBarFilterOption | null>(null);
  const [resolvingLabels, setResolvingLabels] = useState(false);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const selectedLabel = selectedOption?.value === selectedValue ? selectedOption.label : selectedValue;
  const inputValue = searchValue ?? selectedLabel;
  const query = searchValue ?? '';
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const [searchState, setSearchState] = useState<AutosuggestionSearchState | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listboxId = useId();
  const selectionKey = JSON.stringify(value);
  useEffect(() => setSearchValue(null), [filter.id, selectionKey]);

  useEffect(() => {
    let active = true;
    setSelectedOption(null);
    setResolvingLabels(Boolean(selectedValue && resolveOption));

    if (resolveOption && selectedValue) {
      const resolveLabels = async () => {
        try {
          const option = await resolveOption(selectedValue);
          if (active) setSelectedOption(option);
        } catch {
        } finally {
          if (active) setResolvingLabels(false);
        }
      };
      void resolveLabels();
    }

    return () => {
      active = false;
    };
  }, [resolveOption, selectedValue]);

  useEffect(() => {
    let active = true;
    const debouncedQueryMeetsMinimumLength = debouncedQuery.trim().length >= minSearchLength;
    setActiveIndex(-1);

    if (!debouncedQueryMeetsMinimumLength) {
      setSearchState({ query: debouncedQuery, status: AutosuggestionSearchStatus.IDLE, options: [] });
      return () => {
        active = false;
      };
    }

    setSearchState({ query: debouncedQuery, status: AutosuggestionSearchStatus.LOADING, options: [] });
    const searchOptions = async () => {
      try {
        const loadedOptions = await loadOptions(debouncedQuery);
        if (active) {
          setSearchState({ query: debouncedQuery, status: AutosuggestionSearchStatus.SUCCESS, options: loadedOptions });
        }
      } catch {
        if (active) setSearchState({ query: debouncedQuery, status: AutosuggestionSearchStatus.ERROR, options: [] });
      }
    };
    void searchOptions();

    return () => {
      active = false;
    };
  }, [debouncedQuery, loadOptions, minSearchLength]);

  const selectOption = (option: FilterBarFilterOption) => {
    if (multiple) {
      const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
      onChange([...new Set([...selectedValues, option.value])]);
    } else {
      onChange(option.value);
    }
    onClose();
  };

  const currentQueryMeetsMinimumLength = query.trim().length >= minSearchLength;
  const currentSearch =
    currentQueryMeetsMinimumLength && query === debouncedQuery && searchState?.query === query ? searchState : null;
  const loading =
    currentQueryMeetsMinimumLength && (!currentSearch || currentSearch.status === AutosuggestionSearchStatus.LOADING);
  const visibleOptions = currentSearch?.status === AutosuggestionSearchStatus.SUCCESS ? currentSearch.options : [];
  const hasNoResults = currentSearch?.status === AutosuggestionSearchStatus.SUCCESS && visibleOptions.length === 0;
  const hasSearchError = currentSearch?.status === AutosuggestionSearchStatus.ERROR;
  const activeOption = visibleOptions[activeIndex];
  const expanded = loading || visibleOptions.length > 0 || hasNoResults || hasSearchError;

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        if (!visibleOptions.length) return;
        event.preventDefault();
        setActiveIndex(current => (current + 1) % visibleOptions.length);
        break;
      case 'ArrowUp':
        if (!visibleOptions.length) return;
        event.preventDefault();
        setActiveIndex(current => (current <= 0 ? visibleOptions.length - 1 : current - 1));
        break;
      case 'Enter': {
        const option = visibleOptions[activeIndex];
        if (!option) return;
        event.preventDefault();
        selectOption(option);
        break;
      }
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        onClose();
        break;
      default:
        break;
    }
  };

  return (
    <Box>
      <AutosuggestionInput
        label={label}
        value={inputValue}
        resolvingLabel={searchValue === null && resolvingLabels}
        expanded={expanded}
        listboxId={listboxId}
        activeDescendant={activeOption ? `${listboxId}-option-${activeIndex}` : undefined}
        onChange={inputText => {
          setSearchValue(inputText);
          setActiveIndex(-1);
        }}
        onKeyDown={handleInputKeyDown}
      />
      <AutosuggestionResults
        id={listboxId}
        label={label}
        loading={loading}
        options={visibleOptions}
        value={value}
        activeIndex={activeIndex}
        hasNoResults={hasNoResults}
        hasSearchError={hasSearchError}
        onActiveIndexChange={setActiveIndex}
        onSelect={selectOption}
      />
    </Box>
  );
};

export default AutosuggestionFilterEditor;
